import React from 'react';
import { Link } from 'react-router-dom';
import {
  Car, Shield, Zap, FileText, Clock, TrendingUp, Users, Briefcase,
  Home, Heart, Gift, RefreshCw, ArrowUpCircle, Lightbulb, Star, Phone, Mail,
  MapPin, AlertTriangle, Headphones, Building, DollarSign, Percent, Calendar,
  CreditCard, Smartphone, Award, ThumbsUp, Handshake, BookOpen, Globe, Lock,
  CheckCircle, HelpCircle, DollarSign as MoneyIcon
} from 'lucide-react';

const NewCarLoanGhazipur = () => {
  return (
    <div className="bg-gray-50">
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-green-800 to-green-700 text-white py-12 md:py-16 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 animate-fade-in-up">
                New Car Loan in Ghazipur
              </h1>
              <p className="text-lg md:text-xl mb-6 animate-fade-in-up animation-delay-200">
                Drive your dream car with low‑interest rates, quick approval, and minimal paperwork.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-400">
                <button className="bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-semibold px-6 py-2 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg">
                  Apply Now
                </button>
                <button className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold px-6 py-2 rounded-full transition-all duration-300">
                  Check Eligibility
                </button>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img
                src="https://images.unsplash.com/photo-1494976388531-d1058494cdd8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="New Car"
                className="max-w-full h-auto rounded-2xl shadow-xl transition-transform duration-500 hover:scale-105"
              />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-12">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="white"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* About New Car Loan */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">New Car Loan</h2>
          <div className="bg-white rounded-xl shadow-md p-8">
            <p className="text-gray-700 leading-relaxed">
              In India, owning a car is considered still a luxury. But by owning a car, you can upgrade your standard of living. Wondering how? Well,  CashPro Financial Services is here with their low‑interest new car loans. If you are a salaried or a self‑employed person, then you can easily seek a new car loan in Ghazipur and get, set, and drive the car of your dreams carefree.
            </p>
            <div className="text-center mt-6">
              <button className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full">Apply now</button>
            </div>
          </div>
        </div>

        {/* Features & Benefits */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Percent, title: "100% On‑road Funding", desc: "Loan covers the entire ex‑showroom price of the car." },
              { icon: Zap, title: "Seamless Approvals", desc: "Avoid queues and piles of paperwork – quick approval." },
              { icon: Shield, title: "No Collateral Required", desc: "Unsecured loan – no need to pledge land or jewellery." },
              { icon: RefreshCw, title: "Happily Prepay", desc: "Foreclosure allowed; settle your loan without hassle." },
              { icon: Clock, title: "Flexible Tenure", desc: "Repayment period up to 7 years." },
              { icon: TrendingUp, title: "Competitive Rates", desc: "Interest rates starting from 7.50% p.a." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <item.icon className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Documents Required */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Documents Required for a Car Loan</h2>
          <p className="text-gray-600 mb-4">Only a minimum number of documents are to be attached to a car loan application:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Identity Proof: Aadhaar card, PAN card, Driving license, Passport, Voter ID",
              "Address Proof: Aadhaar, Utility bills (not older than 3 months), Passport",
              "Income Proof (Salaried): Salary slips (last 3 months), Bank statement (6 months), Form 16",
              "Income Proof (Self‑employed): ITR (last 2 years), Profit & Loss statement, Bank statement"
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-green-600 mt-1" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Simple Steps */}
        <div className="mb-16 bg-gradient-to-r from-green-50 to-green-50 rounded-xl p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Avail Loan in 4 Simple Steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "1", title: "Check Eligibility", desc: "Use our online eligibility calculator." },
              { step: "2", title: "Decide Amount", desc: "Choose the borrowing amount that suits you." },
              { step: "3", title: "Approach Us", desc: "Online or offline – we handle paperwork." },
              { step: "4", title: "Seek Approval", desc: "Get quick approval on your application." }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Things to Know */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Things to Know While Availing a New Car Loan</h2>
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <MoneyIcon className="w-6 h-6 text-green-600 mt-1" />
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-1">What interest rate are you being charged?</h3>
                <p className="text-gray-600">Compare rates across banks and NBFCs before you finalise.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-6 h-6 text-green-600 mt-1" />
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-1">For how long can you borrow?</h3>
                <p className="text-gray-600">Choose a longer tenure to keep EMIs small. Consider repayment period before signing.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CreditCard className="w-6 h-6 text-green-600 mt-1" />
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-1">How flexibly can you repay the loan?</h3>
                <p className="text-gray-600">Avoid platform fees by using acceptable payment methods. Check for prepayment charges.</p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: "How to get a new car loan?", a: "Check eligibility online, submit documents, get approval, and drive your car." },
              { q: "What is the highest loan amount I can get with a new car loan?", a: "Up to 100% of the on‑road price, depending on your credit profile and income." },
              { q: "Why apply for a new car loan through  CashPro Financial Services?", a: "Lowest interest rates, zero fees, free doorstep service, and quick disbursal." },
              { q: "How does a fixed rate car loan differ from a floating rate car loan?", a: "Fixed rate remains constant; floating rate changes with market conditions. Choose based on your preference." },
              { q: "Is the rate of interest negotiable?", a: "Yes, we can help you negotiate based on your credit score and relationship with the lender." }
            ].map((item, idx) => (
              <div key={idx} className="border-b border-gray-200 pb-3">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">{item.q}</h3>
                    <p className="text-gray-600">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
          <h2 className="text-3xl font-bold text-green-700 mb-4">We Are Here to Help You</h2>
          <p className="text-gray-600 mb-6">Our car loan experts are ready to assist you.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-full">Apply Now</button>
            </div>
            <div>
              <a href="tel:+918887141052" className="text-lg font-bold text-green-600">+91 88871 41052</a>
            </div>
            <div>
              <a href="mailto:info@cashpro.co.in" className="text-lg font-bold text-green-600">info@cashpro.co.in</a>
            </div>
          </div>
          <Link to="/contact" className="text-green-600 hover:underline font-medium">Contact Us →</Link>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up { animation: fade-up 0.6s ease-out forwards; }
        .animate-fade-in-up { animation: fade-up 0.6s ease-out forwards; }
        .animation-delay-200 { animation-delay: 0.2s; }
        .animation-delay-400 { animation-delay: 0.4s; }
      `}</style>
    </div>
  );
};

export default NewCarLoanGhazipur;